import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import MediaComponent from '../Media/media-component';

export default function InfoBarSection({ cards: propCards, alwaysScroll = false }) {
    const systemData = useSelector((state) => state.systemData || {});
    const { dir } = systemData;

    const [cards, setCards] = useState([]);

    const marqueeRef = useRef(null);
    const rafRef = useRef(null);
    const isPaused = useRef(false);

    const scroll = useCallback(() => {
        const el = marqueeRef.current;
        if (!el || isPaused.current) { rafRef.current = requestAnimationFrame(scroll); return; }
        el.scrollLeft += 1.4;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
        rafRef.current = requestAnimationFrame(scroll);
    }, []);

    useEffect(() => {
        if (!cards.length) return;
        rafRef.current = requestAnimationFrame(scroll);
        return () => cancelAnimationFrame(rafRef.current);
    }, [cards, scroll]);

    useEffect(() => {
        if (propCards?.length) {
            setCards(propCards);
        } else {
            SystemDataService.getCardInfo().then((data) => {
                setCards(data || []);
            });
        }
    }, [propCards]);

    if (!cards.length) return null;

    const CardItem = ({ card }) => (
        <div className="flex items-center gap-3 flex-shrink-0 py-4 px-10">
            {card.image && (
                <div className="h-10 max-w-[70px] flex-shrink-0 flex items-center justify-center">
                    <MediaComponent
                        src={card.image}
                        alt={card.title}
                        className="h-10 w-auto object-contain"
                        wrapperClassName="h-10 flex items-center"
                        useSekeleton={false}
                    />
                </div>
            )}
            <div className="flex flex-col leading-tight text-left">
                <span className="text-sm font-bold text-primary-900 text-balance">
                    {card.title}
                </span>
                {(card.subtitle || card.description) && (
                    <span className="text-xs font-normal text-primary-900/70 text-pretty">
                        {card.subtitle || card.description}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div dir={dir} className="w-full bg-gray-50 py-2 px-4 md:px-6">

            {/* Desktop: all items in one row */}
            <div className={`${alwaysScroll ? 'hidden' : 'hidden md:flex'} max-w-[1400px] mx-auto flex-row flex-nowrap justify-center items-center w-full`}>
                {cards.map((card, index) => (
                    <React.Fragment key={card.id || index}>
                        <CardItem card={card} />
                        {index < cards.length - 1 && (
                            <div className="w-px h-12 bg-gray-200 flex-shrink-0" />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Mobile: auto-scroll marquee */}
            <div
                ref={marqueeRef}
                className={`${alwaysScroll ? 'flex' : 'flex md:hidden'} overflow-x-auto scrollbar-hide w-full`}
                onTouchStart={() => { isPaused.current = true; }}
                onTouchEnd={() => { isPaused.current = false; }}
                onMouseEnter={() => { isPaused.current = true; }}
                onMouseLeave={() => { isPaused.current = false; }}
            >
                <div className="flex flex-row flex-nowrap items-center">
                    {[...cards, ...cards].map((card, index) => (
                        <React.Fragment key={index}>
                            <CardItem card={card} />
                            <div className="w-px h-10 bg-gray-200 flex-shrink-0" />
                        </React.Fragment>
                    ))}
                </div>
            </div>

        </div>
    );
}
